import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import { CONTACT_INFO } from "../data";
import { applyPageMeta, PAGE_SEO_CONFIG } from "../lib/seoMeta";

export default function Contact() {
  useEffect(() => {
    // Set metadata for Kontakt os (max 512px title, max 970px description)
    applyPageMeta(PAGE_SEO_CONFIG.contact);
  }, []);

  return (
    <div className="bg-white text-neutral-900 min-h-screen py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider block mb-2">
            Kontakt os
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-neutral-900">
            Vi sidder klar til at hjælpe dig
          </h1>
          <p className="mt-4 text-lg text-neutral-600">
            Har du spørgsmål til et projekt, eller ønsker du et uforpligtende tilbud? Hos{" "}
            <Link
              to="/"
              className="text-yellow-600 hover:text-yellow-700 font-bold underline underline-offset-2"
              title="Gå til forsiden for JG Mesterbyg"
            >
              JG Mesterbyg
            </Link>{" "}
            står vi klar til at hjælpe dig på Mors og i hele Nordjylland. Du kan også udfylde vores{" "}
            <Link
              to="/fa-tilbud"
              className="text-yellow-600 hover:text-yellow-700 font-semibold hover:underline"
            >
              tilbudsformular her
            </Link>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact Details */}
          <div className="lg:col-span-6 space-y-8">
            {/* Contact Card */}
            <div className="bg-neutral-50 p-8 rounded-2xl border border-neutral-200 shadow-sm space-y-6">
              <h2 className="text-xl font-bold text-neutral-900 border-b border-neutral-200 pb-3 mb-4">
                Kontaktoplysninger
              </h2>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <h3 className="font-bold text-neutral-900 text-sm">Ring til os</h3>
                  <p className="text-xs text-neutral-500 mb-1">Vi svarer alle hverdage.</p>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="text-yellow-600 font-bold hover:text-yellow-700 transition-colors text-sm"
                  >
                    {CONTACT_INFO.phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <h3 className="font-bold text-neutral-900 text-sm">Send en e-mail</h3>
                  <p className="text-xs text-neutral-500 mb-1">Vi svarer inden for 24 timer.</p>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="text-yellow-600 font-bold hover:text-yellow-700 transition-colors text-sm break-all"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-xl bg-yellow-500/10 text-yellow-600 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div className="self-center">
                  <span className="text-neutral-700 font-semibold text-sm">
                    {CONTACT_INFO.address}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-6 relative">
            <div className="absolute inset-0 bg-yellow-500/10 blur-3xl rounded-full -z-10" />
            <ContactForm source="Kontaktside Formular" buttonText="Send besked" />
          </div>
        </div>

        {/* Google Map Embed (Mors, Denmark) - Aligned with primary content and rounded corners */}
        <div className="mt-16 lg:mt-24 w-full h-[450px] rounded-2xl border border-neutral-200 shadow-sm overflow-hidden relative">
          <iframe
            title="JG Mesterbyg Adresse"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2197.632230113825!2d8.751659377402095!3d56.83549297327334!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464a38e8a7199047%3A0x2424b9e829399047!2sPr%C3%A6stbrovej%20173%2C%207950%20Erslev!5e0!3m2!1sda!2sdk!4v1719900000000!5m2!1sda!2sdk"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </div>
  );
}
